import { ArrowBack, MoreVert } from '@mui/icons-material'
import { Box, IconButton, Skeleton, Stack, SvgIcon, Typography } from '@mui/material'
import Image from 'next/image'
import { FC, Fragment } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { InternalLinkIconButton } from '@omnea-app/fe-routing/InternalLinkIconButton'
import { CopyConfigButton } from '@omnea-app/frontend/components/shared/ui/CopyConfigButton/CopyConfigButton'
import { CopyIdButton } from '@omnea-app/frontend/components/shared/ui/CopyIdButton/CopyIdButton'
import ProductPlaceholderIcon from '@omnea-app/icons/common/product-placeholder.svg'
import RenewalPlaceholderIcon from '@omnea-app/icons/common/renewal-placeholder.svg'
import { usePriceFormatter } from '@omnea-app/legacy/utils/price/usePriceFormatter'
import { RequestTypeEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { Price } from '@omnea-app/requests/price'
import { RequestDetail } from '@omnea-app/requests/requestsFrontendClient'
import { generateRequestsListRoute } from '../../../requestRoutes'
import { EstimatedCompletionBanner } from '../estimated-completion-banner'
import { ManageMenu, ManageMenuAnchor } from '../manage-menu'

export type RequestHeaderData = Pick<RequestDetail, 'value' | 'participants'> & {
  derivedFields: Pick<
    RequestDetail['derivedFields'],
    'product' | 'supplier' | 'model' | 'ownerId' | 'currentUserAssignees' | 'currentGroupAssignees'
  >
  rawData: Pick<RequestDetail['rawData'], 'publicId' | 'name' | 'completedAt' | 'state' | 'type' | 'requestorUserId'>
}

type RequestHeaderProps = {
  request?: RequestHeaderData
  isLoading: boolean
  requestTitle: string
  isRequestRejected: boolean
  isRequestCompleted: boolean
  requestId: string
  onRejectRequest: (reason: string) => void
  onUnrejectRequest: (reason: string, productId?: string) => void
  onDeleteRequest: () => void
}

const ManageMenuButton: ManageMenuAnchor = ({ buttonRef, openMenu }) => (
  <IconButton sx={{ color: 'neutralLight.900', p: 0 }} ref={buttonRef} onClick={openMenu}>
    <MoreVert />
  </IconButton>
)

export function useFormatRequestValue(value: Price | null | undefined) {
  const { formatPrice } = usePriceFormatter()
  if (!value) return null
  return formatPrice(value).formattedPrice
}

const ProductLogo = ({
  product,
  supplier,
  requestTitle,
  isRenewal,
}: {
  product: { logoUrl?: string | null; name?: string } | null | undefined
  supplier: { logoUrl?: string | null; name?: string } | null | undefined
  requestTitle: string
  isRenewal: boolean
}) => {
  const alt = product?.name || requestTitle

  const logoUrl = product?.logoUrl || supplier?.logoUrl

  if (!logoUrl) {
    return (
      <Stack
        data-testid="placeholder"
        sx={{
          borderRadius: 3,
          backgroundColor: 'neutralLight.500',
          color: 'neutralLight.0',
          padding: 4,
          alignSelf: 'center',
        }}
      >
        <SvgIcon
          component={isRenewal ? RenewalPlaceholderIcon : ProductPlaceholderIcon}
          inheritViewBox
          aria-hidden="true"
          sx={{ fontSize: 24 }}
        />
      </Stack>
    )
  }
  return (
    <Box
      sx={{
        borderRadius: '6px',
        border: '1px solid',
        borderColor: 'neutralLight.0',
        overflow: 'hidden',
        backgroundColor: 'white',
        alignSelf: 'center',
      }}
    >
      <Image alt={alt} src={logoUrl} style={{ objectFit: 'contain' }} width={42} height={42} />
    </Box>
  )
}

const RequestTitle = ({ requestTitle }: { requestTitle: string }) => (
  <Typography
    variant="h2"
    fontWeight={600}
    sx={{
      whiteSpace: { md: 'nowrap' },
      textAlign: { xs: 'center', md: 'left' },
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      color: 'neutralDark.100',
    }}
    data-testid={`request-title-${requestTitle}`}
  >
    {requestTitle}
  </Typography>
)

export const RequestHeader: FC<RequestHeaderProps> = ({
  request,
  isLoading,
  requestTitle,
  isRequestRejected,
  isRequestCompleted,
  requestId,
  onDeleteRequest,
  onRejectRequest,
  onUnrejectRequest,
}) => {
  const product = request?.derivedFields?.product
  const supplier = request?.derivedFields?.supplier

  const subtitleDetails = [
    { text: supplier?.name, testId: 'request-supplier' },
    { text: useFormatRequestValue(request?.value), testId: 'request-value' },
    { text: request?.rawData.publicId, testId: 'request-public-id' },
  ].filter((detail) => !!detail.text)

  const { user } = useAuthenticatedUser()

  return (
    <Stack
      gap={{ xs: 18, md: 4 }}
      direction={{ xs: 'column-reverse', md: 'row' }}
      alignItems={{ xs: 'stretch', md: 'center' }}
      sx={{ justifyContent: 'space-between' }}
      pt={{ md: 3 }}
      pb={{ xs: 18, md: 12 }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={5} sx={{ flex: 1, minWidth: 0 }}>
        {isLoading ? (
          <Skeleton variant="rectangular" sx={{ borderRadius: '8px' }} width={350} height={40} />
        ) : (
          <>
            <ProductLogo
              product={product}
              supplier={supplier}
              requestTitle={requestTitle}
              isRenewal={request?.rawData.type === RequestTypeEnum.RENEWAL}
            />
            <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
              <RequestTitle requestTitle={requestTitle} />

              {subtitleDetails.length || user.isOmnean ? (
                <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" gap={3}>
                  <Typography variant="Body14pxMedium" color="neutralLight.900" textAlign={{ xs: 'center', md: 'left' }}>
                    {subtitleDetails.map((detail, index) => (
                      <Fragment key={index}>
                        {index > 0 && <> ∙ </>}
                        <span data-testid={detail.testId}>{detail.text}</span>
                      </Fragment>
                    ))}
                  </Typography>
                  <Stack direction="row" gap={1} my={-2}>
                    <CopyIdButton id={requestId} entityTypeLabel="request" color="neutralLight.900" />
                    <CopyConfigButton config={request?.derivedFields.model} entityTypeLabel="request" color="neutralLight.900" />
                  </Stack>
                </Stack>
              ) : null}
            </Stack>
          </>
        )}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        gap={4}
        justifyContent="space-between"
        sx={{
          height: { md: '38px' }, // Title font-size * title line-height
          alignSelf: { md: 'flex-start' },
        }}
      >
        <InternalLinkIconButton href={generateRequestsListRoute()} sx={{ display: { md: 'none' }, padding: 0 }} size="small">
          <ArrowBack />
        </InternalLinkIconButton>
        {!isLoading && !isRequestCompleted && !isRequestRejected && <EstimatedCompletionBanner requestId={requestId} />}
        {isLoading && <Skeleton variant="rectangular" sx={{ borderRadius: '8px' }} width={240} height={36} />}
        <ManageMenu
          requestId={requestId}
          request={request}
          onDeleteRequest={onDeleteRequest}
          onRejectRequest={onRejectRequest}
          onUnrejectRequest={onUnrejectRequest}
          anchor={ManageMenuButton}
        />
      </Stack>
    </Stack>
  )
}
